import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useNotifications from "../../hooks/useNotifications";
import NotificationItem from "../../components/notifications/NotificationItem";
import { getNotificationPath } from "../../utils/notificationRoutes";

/**
 * Full inbox page — owner (/notifications) and staff (/staff-panel/notifications).
 */
export default function NotificationsPage({ isPortal = false }) {
  const navigate = useNavigate();
  const {
    notifications,
    loading,
    error,
    pagination,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    removeNotification,
    active,
    isStaff,
  } = useNotifications();

  const [filter, setFilter] = useState("all"); // all | unread
  const portal = isPortal || isStaff;

  useEffect(() => {
    if (!active) return;
    const params = { page: 1, limit: 50 };
    if (filter === "unread") params.isRead = false;
    fetchNotifications(params);
  }, [active, filter, fetchNotifications]);

  const handleItemClick = async (item) => {
    if (!item) return;
    if (!item.isRead) {
      await markAsRead(item._id);
    }
    const path = getNotificationPath(item, portal);
    if (path) navigate(path);
  };

  if (!active) {
    return (
      <div className="rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-6 text-sm text-neutral-500 dark:text-neutral-400">
        Notifications are not available for this account type yet.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-title-lg text-neutral-900 dark:text-neutral-100">
            Notifications
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {pagination?.total != null
              ? `${pagination.total} total`
              : portal
                ? "Staff inbox"
                : "School inbox"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setFilter("all")}
              className={`rounded-md px-3 py-1.5 ${ filter === "all" ? "bg-primary-600 text-white" : "text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800" }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setFilter("unread")}
              className={`rounded-md px-3 py-1.5 ${ filter === "unread" ? "bg-primary-600 text-white" : "text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800" }`}
            >
              Unread
            </button>
          </div>
          <button
            type="button"
            onClick={() => markAllAsRead()}
            className="rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-3 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:bg-neutral-50 dark:hover:bg-neutral-800"
          >
            Mark all read
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-sm">
        
        {loading && notifications.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-neutral-400 dark:text-neutral-500">
            Loading notifications…
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-neutral-400 dark:text-neutral-500">
            No notifications to show
          </div>
        ) : (
          <ul>
            {notifications.map((n) => (
              <NotificationItem
                key={n._id}
                notification={n}
                compact={false}
                onClick={handleItemClick}
                onDelete={(item) => removeNotification(item._id)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
